import { LockKey, WifiSlash } from '@phosphor-icons/react';
import type { MeResponse, Permission, PlatformRole } from '@brechoflow/contracts';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { NotFoundPage } from '@/pages/not-found-page';
import { useSession } from './session';

// página de venda: quem chega em "/" sem sessão vê o produto, não um formulário de login
const LandingPage = lazy(() => import('@/features/landing/landing-page').then((m) => ({ default: m.LandingPage })));

export function FullScreenLoading() {
  return (
    <div role="status" aria-label="Carregando" className="grid min-h-dvh place-items-center bg-background">
      <div className="flex w-64 flex-col gap-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    </div>
  );
}

/** Rotas que exigem sessão. Sem sessão, vai para /entrar guardando o destino em `?next=`. */
export function RequireAuth() {
  const { status, me, membership, refetch } = useSession();
  const loc = useLocation();
  if (status === 'loading') return <FullScreenLoading />;
  if (status === 'error') {
    return (
      <div className="grid min-h-dvh place-items-center bg-background p-6">
        <EmptyState
          icon={WifiSlash}
          title="Não foi possível conectar"
          description="Verifique sua conexão com a internet e tente de novo."
          action={<Button onClick={() => void refetch()}>Tentar de novo</Button>}
        />
      </div>
    );
  }
  if (status === 'anonymous') {
    if (loc.pathname === '/' && !loc.search) {
      return (
        <Suspense fallback={<FullScreenLoading />}>
          <LandingPage />
        </Suspense>
      );
    }
    const next = loc.pathname + loc.search;
    return <Navigate to={next === '/' ? '/entrar' : `/entrar?next=${encodeURIComponent(next)}`} replace />;
  }
  // operador da plataforma não tem loja: quem barra as telas de tenant é o RequireTenant
  if (!membership && !isOperator(me)) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background p-6">
        <EmptyState icon={LockKey} title="Sem loja ativa" description="Sua conta não está vinculada a nenhuma loja no momento. Peça um novo convite a quem administra a loja." />
      </div>
    );
  }
  return <Outlet />;
}

/** Operador da plataforma fora de impersonação: nunca usa as telas de loja. */
export function isOperator(me: MeResponse | null): boolean {
  return Boolean(me?.platformRole) && !me?.impersonation;
}

/** Rotas de loja (tudo fora de `/plataforma`): operador sem impersonação volta para `/plataforma`. */
export function RequireTenant() {
  const { me, membership } = useSession();
  const loc = useLocation();
  if (isOperator(me) || (!membership && me?.home === '/plataforma')) return <Navigate to="/plataforma" replace />;
  // senha definida pelo operador: até trocá-la, só a tela Minha conta (seção Senha) fica acessível
  if (mustChangePassword(me) && loc.pathname !== '/conta') return <Navigate to="/conta#senha" replace />;
  // depois da senha (que tem prioridade), o usuário que ainda não aceitou a versão vigente dos Termos/Política aceita
  if (!mustChangePassword(me) && needsTermsAcceptance(me)) return <Navigate to="/aceitar-termos" replace />;
  return <Outlet />;
}

/**
 * A versão aceita dos Termos/Política não é a vigente. Vale só para usuário de loja: o operador não é barrado
 * e, dentro de uma impersonação, nunca se aceita em nome de outra pessoa.
 */
export function needsTermsAcceptance(me: MeResponse | null): boolean {
  return Boolean(me) && me!.termsAccepted === false && !me!.impersonation && !isOperator(me);
}

/** A senha atual foi definida pelo operador e precisa ser trocada. Dentro de uma impersonação o operador não é barrado. */
export function mustChangePassword(me: MeResponse | null): boolean {
  return Boolean(me?.mustChangePassword) && !me?.impersonation;
}

/** Telas públicas (entrar, cadastro): quem já tem sessão vai direto para o app. */
export function GuestOnly() {
  const { status, me } = useSession();
  const [params] = useSearchParams();
  if (status === 'loading') return <FullScreenLoading />;
  if (status === 'authenticated') return <Navigate to={landingFor(me, params.get('next'))} replace />;
  return <Outlet />;
}

/** Aceita apenas caminhos internos (evita open redirect). */
export function safeNext(next: string | null | undefined): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Destino após autenticar: o `?next=` interno, senão a home do usuário (`/plataforma` para operador). */
export function landingFor(me: MeResponse | null, next: string | null | undefined): string {
  return next ? safeNext(next) : (me?.home ?? '/');
}

export function Forbidden() {
  return <EmptyState icon={LockKey} title="Você não tem acesso a esta área" description="Peça a quem administra a loja para liberar a permissão necessária no seu papel." />;
}

/** Rota que exige permissão (qualquer uma da lista). */
export function RequirePermission({ permission, children }: { permission: Permission | Permission[]; children?: ReactNode }) {
  const { permissions } = useSession();
  const list = Array.isArray(permission) ? permission : [permission];
  if (!list.some((p) => permissions.has(p))) return <Forbidden />;
  return children ?? <Outlet />;
}

/** Renderiza os filhos só se houver a permissão. */
export function Can({ permission, children, fallback = null }: { permission: Permission | Permission[]; children: ReactNode; fallback?: ReactNode }) {
  const { permissions } = useSession();
  const list = Array.isArray(permission) ? permission : [permission];
  return list.some((p) => permissions.has(p)) ? children : fallback;
}

/** Papel do usuário no admin da plataforma (null para quem só usa uma loja). */
export function usePlatformRole(): PlatformRole | null {
  const { me } = useSession();
  return me?.platformRole ?? null;
}

/**
 * Rotas do admin da plataforma. Sem papel de plataforma a área nem "existe" (404);
 * com `roles`, quem tem papel mas não está na lista vê "sem acesso".
 */
export function RequirePlatformAdmin({ roles, children }: { roles?: readonly PlatformRole[]; children?: ReactNode }) {
  const role = usePlatformRole();
  if (!role) return <NotFoundPage />;
  if (roles && !roles.includes(role)) return <Forbidden />;
  return children ?? <Outlet />;
}
